import "./AutoMLPage.css";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest, logout } from "../utils/api";

function AutoMLPage() {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [time, setTime] = useState("");
  const navigate = useNavigate();
  const MAX_FILE_SIZE_MB = 10;

  React.useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = new Date();
      const hours = currentTime.getHours() % 12 || 12;
      const minutes = currentTime.getMinutes().toString().padStart(2, "0");
      const seconds = currentTime.getSeconds().toString().padStart(2, "0");
      const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
      setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 KB';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      // Check file extension
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (fileExtension !== 'csv') {
        setUploadStatus('Invalid file type. Only CSV files are allowed.');
        setFile(null);
        return;
      }

      if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setUploadStatus(`File size exceeds ${MAX_FILE_SIZE_MB}MB. Please upload a smaller file.`);
        setFile(null);
        return;
      }

      setFile(selectedFile);
      setUploadStatus('');
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      setUploadStatus('Please select a file to upload.');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const result = await apiRequest('/api/upload', {
        method: 'POST',
        body: formData,
        contentType: 'multipart/form-data',
      });
      setIsUploading(false);

      if (result.status === 'success') {
        navigate('/dataset-preview'); // Navigate to preview page after upload
      } else {
        setUploadStatus(result.message || result.detail);
      }
    } catch (error) {
      setIsUploading(false);
      setUploadStatus(`Error: ${error.message}`);
    }
  };

  return (
    <div className="automl-page">
      {/* Top Navigation Bar */}
      <div className="top-nav">
        <div className="nav-content">
          <div className="nav-left-group">
            <div className="nav-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
              <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
            </div>
            <button className="back-nav-button" onClick={() => navigate("/dashboard")}>
              Dashboard
            </button>
          </div>
          <div className="nav-right">
            <div className="digital-clock">{time}</div>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>Upload Your Healthcare Dataset</h1>
          <p className="header-subtitle">Upload a CSV file to begin analyzing your healthcare data</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">

        {/* Upload Section */}
        <div className="upload-section">
          <div className="upload-card">
            <div className="upload-icon icon-folder"></div>
            <h2>Select CSV File</h2>
            <p className="upload-hint">Maximum file size: {MAX_FILE_SIZE_MB}MB</p>
            
            <div className="file-upload-area">
              <input
                type="file"
                id="file-input"
                onChange={handleFileChange}
                accept=".csv"
                className="file-input-hidden"
              />
              <label htmlFor="file-input" className="file-input-label">
                {file ? (
                  <div className="file-selected">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">({formatFileSize(file.size)})</span>
                  </div>
                ) : (
                  <div className="file-placeholder">
                    <span className="upload-icon-large icon-file"></span>
                    <span>Click to select or drag and drop</span>
                    <span className="file-type-hint">CSV files only</span>
                  </div>
                )}
              </label>
            </div>

            <button
              onClick={handleFileUpload}
              disabled={isUploading || !file}
              className="upload-button"
            >
              {isUploading ? 'Uploading...' : 'Upload Dataset'}
            </button>

            {uploadStatus && (
              <div className={`upload-status ${uploadStatus.includes('Error') || uploadStatus.includes('Invalid') ? 'error' : 'success'}`}>
                {uploadStatus}
              </div>
            )}
          </div>
        </div>

        {/* Guidelines Section */}
        <div className="guidelines-section">
          <h2>Dataset Requirements</h2>
          <p className="guidelines-intro">Ensure your healthcare CSV dataset meets these requirements for optimal analysis:</p>

          <div className="guidelines-grid">
            <div className="guideline-card">
              <div className="guideline-icon icon-check"></div>
              <h3>Supervised Learning</h3>
              <ul>
                <li>Must contain a target/output column</li>
                <li>Input features should be numerical</li>
                <li>Encode categorical variables before uploading</li>
                <li>At least 100 rows recommended</li>
                <li>Handle missing values appropriately</li>
              </ul>
            </div>

            <div className="guideline-card">
              <div className="guideline-icon icon-check"></div>
              <h3>Unsupervised Learning</h3>
              <ul>
                <li>No target/output column required</li>
                <li>Include relevant features only</li>
                <li>Categorical and numerical variables supported</li>
                <li>Feature scaling improves performance</li>
                <li>Avoid unique identifiers (IDs, names)</li>
              </ul>
            </div>
          </div>

          <div className="info-box">
            <h3>Healthcare Data Tips</h3>
            <p>Perfect for: patient records, clinical trial data, research datasets, medical survey results, treatment outcomes, and more.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AutoMLPage;
